/**
 * Product analytics for People Space, sent DIRECTLY to Amplitude.
 *
 * ── Why this exists next to segment.ts ───────────────────────────────────────────────────────
 * FINN's documented architecture routes analytics through ONE Segment source, which fans out to
 * Amplitude and the data warehouse (see CLAUDE.md). That remains the org standard and `segment.ts`
 * stays wired for it. This module is a DELIBERATE, reversible deviation: creating the Segment
 * source needs console access the People team does not hold, while the Amplitude project key was
 * already in hand, so people-space would otherwise have shipped with no product analytics at all.
 *
 * The tradeoff this accepts: events reach Amplitude but NOT the data warehouse. Anything
 * downstream that expects people-space events in BigQuery needs Amplitude's own warehouse export,
 * or the Segment leg finished. Both sinks can run at once — `nativeAnalytics.ts` fans out to every
 * live sink — so adopting Segment later is an addition, not a migration, and the Amplitude history
 * carries over because the event names do not change.
 *
 * Target is Amplitude project **785010 ("FINN Spaces")**, SHARED with the spaces app — not a
 * dedicated project. See `APP_TAG` below for what that costs and how charts stay honest.
 *
 * ── Configuration ────────────────────────────────────────────────────────────────────────────
 * One env var, `VITE_AMPLITUDE_API_KEY`. A browser API key is a PUBLIC identifier, like the
 * Firebase config already in this repo — but it stays env-driven so local dev sends nothing by
 * default. Without it every function here is a silent no-op.
 *
 * FINN's Amplitude org (`finn-auto`) is on the **US** instance, which is the SDK default, so no
 * `serverZone` is set. If the org ever moves to EU, events would silently stop arriving rather
 * than error — set `serverZone: 'EU'` here at that point.
 *
 * ── PII rule (identical to segment.ts) ───────────────────────────────────────────────────────
 * NO candidate data may reach analytics — no names, emails, subjects or message text. Track
 * categories, counts, booleans, durations. The signed-in RECRUITER's email is the user id; the
 * candidates on the public booking pages are never identified at all.
 */
import * as amplitude from '@amplitude/analytics-browser';
import { isBlockedHost as isLocalHost } from './hostGuard';
import { currentPath, redactUrlLike } from './redactPath';

const API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY || '';

/**
 * Stamped on every event, because people-space shares Amplitude project 785010 with the FINN
 * Spaces app (~2,000 event types) rather than owning a project of its own.
 *
 * Four event names — `hiring_dashboard_viewed`, `hiring_dashboard_refreshed`,
 * `reference_letters_viewed`, `equity_calculator_viewed` — ALREADY hold data there, from back when
 * the people board lived inside the spaces app. That is useful continuity across the repo split,
 * not a mistake, but it means a chart on those names mixes two eras. Filter on `app` to separate
 * them; omit the filter to see the metric's full history. It also protects every other event name
 * from a future collision in a taxonomy this large.
 */
const APP_TAG = 'people-space';

/**
 * Also stamped on every event, because there is only ONE Amplitude project and it is the spaces
 * app's PRODUCTION one — there is no staging project to point a dev build at.
 *
 * Verifying analytics locally (`VITE_AMPLITUDE_DEBUG=true`) therefore writes real events into a
 * project other teams read. Tagging them `dev` keeps that honest: production charts filter
 * `env = prod` and a developer clicking through the app cannot quietly inflate someone's numbers.
 * Derived from the host, NOT from a flag, so a dev build can never label itself `prod` by mistake.
 */
const envTag = () => (isLocalHost(undefined) ? 'dev' : 'prod');

/** Kill switch: local dev never sends, even with a key. `VITE_AMPLITUDE_DEBUG=true` opts back in. */
function isBlockedHost(): boolean {
  return isLocalHost(import.meta.env.VITE_AMPLITUDE_DEBUG);
}

let started = false;

/** True when a key is configured and this host may send. */
export const amplitudeEnabled = () => Boolean(API_KEY) && !isBlockedHost();

/**
 * Scrub booking tokens out of anything the SDK attaches by itself.
 *
 * Autocapture is off (see `init`), so in principle no URL-bearing property should reach this. It
 * runs anyway because the failure mode is not recoverable: the `/book/:token` URL IS a candidate's
 * authorisation, and an SDK upgrade that turns a capture feature back on by default would start
 * exporting live tokens with no code change on our side and no error to notice. A property that
 * never contains `/book/` is returned untouched, so the cost is one string check per property.
 */
const redactionPlugin: amplitude.Types.EnrichmentPlugin = {
  name: 'redact-booking-token',
  type: 'enrichment',
  execute: async (event: amplitude.Types.Event) => {
    // `event_properties` is typed as a union (plain bag | revenue shape), which cannot be
    // index-assigned directly. Every member is a string-keyed bag at runtime, so narrow to that.
    const props = event.event_properties as Record<string, unknown> | undefined;
    if (props) {
      for (const [key, value] of Object.entries(props)) {
        if (typeof value === 'string') props[key] = redactUrlLike(value);
      }
    }
    return event;
  },
};

/**
 * Idempotent; safe to call from anywhere that might run first. The SDK buffers events fired before
 * init completes, so callers never await it. Never throws: analytics being broken must not break
 * the page.
 */
export function initAmplitude(): boolean {
  if (!amplitudeEnabled()) return false;
  if (started) return true;
  try {
    amplitude.init(API_KEY, {
      // OFF, deliberately. Autocaptured page views record the page URL, and on the public
      // `/book/:token` routes that URL is the candidate's booking credential — the exact leak
      // `redactPath` exists to prevent. Page views are sent explicitly by `trackAmplitudePage`
      // with a redacted path instead. Turning any of this on requires re-checking that path.
      autocapture: false,
    });
    amplitude.add(redactionPlugin);
    started = true;
    return true;
  } catch {
    return false;
  }
}

export type EventProps = Record<string, string | number | boolean | null | undefined>;

/** Fire-and-forget event. Callers must respect the PII rule in the module header. */
export function trackAmplitudeEvent(eventName: string, props: EventProps = {}): void {
  if (!initAmplitude()) return;
  try {
    amplitude.track(eventName, { ...props, app: APP_TAG, env: envTag(), path: currentPath() });
  } catch {
    // Analytics failures are non-fatal.
  }
}

/**
 * Tie events to the signed-in recruiter. Pass null on sign-out so a shared browser does not keep
 * attributing to the previous user.
 */
export function identifyAmplitudeUser(email: string | null): void {
  if (!initAmplitude()) return;
  try {
    const id = email?.trim().toLowerCase();
    if (id) amplitude.setUserId(id);
    else amplitude.reset();
  } catch {
    // Analytics failures are non-fatal.
  }
}

/** SPA route change, sent as an explicit event because autocapture is off. Path is redacted. */
export function trackAmplitudePage(path: string): void {
  if (!initAmplitude()) return;
  try {
    amplitude.track('page_viewed', { app: APP_TAG, env: envTag(), path: redactUrlLike(path) });
  } catch {
    // Analytics failures are non-fatal.
  }
}

/** @internal test seam */
export function _resetAmplitudeForTests(): void {
  started = false;
}
